import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Progress, Row, Segmented, Space, Table, Tag, message } from 'antd';
import { Line, Pie } from '@ant-design/plots';
import type { ColumnsType } from 'antd/es/table';
import {
  BarChartOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  FireOutlined,
  ReloadOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { get } from '../../api/request';
import { API_ENDPOINTS, buildApiUrl } from '../../api/config';
import './AnalyticsDataPage.css';

type TrendType = 'up' | 'down';

interface AnalyticsMetricCard {
  key: string;
  title: string;
  value: number;
  suffix: string;
  trend: string;
  trend_type: TrendType;
  desc: string;
  precision: number;
}

interface AnalyticsTrendItem {
  date: string;
  visits: number;
  active_users: number;
  events: number;
  avg_duration: number;
}

interface AnalyticsDistributionItem {
  label: string;
  value: number;
  percent: number;
}

interface AnalyticsFunnelItem {
  step: string;
  value: number;
  rate: number;
}

interface AnalyticsEventItem {
  id: string;
  event: string;
  user: string;
  page: string;
  module: string;
  action: string;
  device: string;
  duration: number;
  time: string;
}

interface AnalyticsOverview {
  generated_at: string;
  range_days: number;
  metrics: AnalyticsMetricCard[];
  trend: AnalyticsTrendItem[];
  event_distribution: AnalyticsDistributionItem[];
  device_distribution: AnalyticsDistributionItem[];
  funnel: AnalyticsFunnelItem[];
  recent_events: AnalyticsEventItem[];
  module_heat: AnalyticsDistributionItem[];
}

interface AnalyticsEventList {
  data: AnalyticsEventItem[];
  total: number;
  page: number;
  page_size: number;
}

const DEFAULT_OVERVIEW: AnalyticsOverview = {
  generated_at: new Date().toISOString(),
  range_days: 7,
  metrics: [
    { key: 'visits', title: '访问会话', value: 0, suffix: '次', trend: '0.0%', trend_type: 'up', desc: '按 session_id 去重统计', precision: 0 },
    { key: 'active_users', title: '活跃用户', value: 0, suffix: '人', trend: '0.0%', trend_type: 'up', desc: '登录用户行为去重统计', precision: 0 },
    { key: 'events', title: '交互事件', value: 0, suffix: '次', trend: '0.0%', trend_type: 'up', desc: '页面浏览、点击、AI创作等事件', precision: 0 },
    { key: 'duration', title: '平均停留', value: 0, suffix: '秒', trend: '0.0%', trend_type: 'up', desc: '用户端上报 duration 均值', precision: 0 },
  ],
  trend: [],
  event_distribution: [],
  device_distribution: [],
  funnel: [],
  recent_events: [],
  module_heat: [],
};

const metricIconMap: Record<string, React.ReactNode> = {
  visits: <EyeOutlined />,
  active_users: <UserSwitchOutlined />,
  events: <BarChartOutlined />,
  duration: <ClockCircleOutlined />,
};

const eventColorMap: Record<string, string> = {
  session_identify: 'blue',
  project_create: 'cyan',
  chapter_save: 'green',
  ai_generate: 'purple',
  view: 'geekblue',
  click: 'orange',
};

const eventLabelMap: Record<string, string> = {
  session_identify: '会话识别',
  project_create: '创建作品',
  chapter_save: '保存章节',
  ai_generate: 'AI 生成',
  page_view: '页面访问',
  page_leave: '离开页面',
  view: '浏览',
  click: '点击',
};

const moduleColorMap: Record<string, string> = {
  user_session: 'blue',
  project: 'cyan',
  chapter: 'green',
  ai_creation: 'purple',
  web: 'geekblue',
};

const formatRefreshTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const downloadJson = (data: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const AnalyticsDataPage: React.FC = () => {
  const [days, setDays] = useState<number>(7);
  const [overview, setOverview] = useState<AnalyticsOverview>(DEFAULT_OVERVIEW);
  const [events, setEvents] = useState<AnalyticsEventItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, eventData] = await Promise.all([
        get<AnalyticsOverview>(buildApiUrl(API_ENDPOINTS.analytics.overview, { days })),
        get<AnalyticsEventList>(buildApiUrl(API_ENDPOINTS.analytics.events, { days, page, page_size: pageSize })),
      ]);
      setOverview({
        ...DEFAULT_OVERVIEW,
        ...overviewData,
        metrics: overviewData.metrics?.length ? overviewData.metrics : DEFAULT_OVERVIEW.metrics,
      });
      setEvents(eventData.data || []);
      setTotal(eventData.total || 0);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载埋点数据失败');
    } finally {
      setLoading(false);
    }
  }, [days, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const trendData = useMemo(() => {
    return overview.trend.flatMap((item) => [
      { date: item.date, type: '访问会话', value: item.visits },
      { date: item.date, type: '活跃用户', value: item.active_users },
      { date: item.date, type: '交互事件', value: item.events },
    ]);
  }, [overview.trend]);

  const maxHeat = useMemo(() => Math.max(1, ...overview.module_heat.map((item) => item.value)), [overview.module_heat]);
  const eventDistribution = useMemo(
    () => overview.event_distribution.map((item) => ({ ...item, label: eventLabelMap[item.label] || item.label })),
    [overview.event_distribution],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await get(buildApiUrl(API_ENDPOINTS.analytics.exportEvents, { days }));
      downloadJson(data, `analytics-events-${days}d.json`);
      message.success('埋点数据已导出');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导出埋点数据失败');
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<AnalyticsEventItem> = [
    {
      title: '事件',
      dataIndex: 'event',
      width: 160,
      render: (value) => <Tag color={eventColorMap[value] || 'default'}>{eventLabelMap[value] || value}</Tag>,
    },
    {
      title: '用户',
      dataIndex: 'user',
      width: 130,
    },
    {
      title: '模块',
      dataIndex: 'module',
      width: 130,
      render: (value) => <Tag color={moduleColorMap[value] || 'default'}>{value}</Tag>,
    },
    {
      title: '动作',
      dataIndex: 'action',
      width: 100,
    },
    {
      title: '页面',
      dataIndex: 'page',
      ellipsis: true,
      render: (value) => <span className="analytics-code">{value}</span>,
    },
    {
      title: '设备',
      dataIndex: 'device',
      width: 100,
    },
    {
      title: '停留',
      dataIndex: 'duration',
      width: 90,
      render: (value) => value > 0 ? `${value}s` : '—',
    },
    {
      title: '时间',
      dataIndex: 'time',
      width: 160,
    },
  ];

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div>
          <h1 className="page-title">埋点数据</h1>
          <p>跟踪用户访问、登录识别、创作路径、AI调用和章节保存等关键行为。</p>
        </div>
        <Space wrap>
          <Segmented
            value={days}
            onChange={(value) => {
              setDays(Number(value));
              setPage(1);
            }}
            options={[
              { label: '近7天', value: 7 },
              { label: '近14天', value: 14 },
              { label: '近30天', value: 30 },
            ]}
          />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={loadData}>刷新</Button>
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>导出</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="analytics-metrics">
        {overview.metrics.map((item) => (
          <Col xs={24} sm={12} xl={6} key={item.key}>
            <Card bordered={false} className="analytics-metric-card">
              <div className="analytics-metric-top">
                <span className="analytics-metric-icon">{metricIconMap[item.key] || <BarChartOutlined />}</span>
                <Tag color={item.trend_type === 'up' ? 'green' : 'blue'}>{item.trend}</Tag>
              </div>
              <span className="analytics-metric-title">{item.title}</span>
              <strong>
                {Number(item.value || 0).toLocaleString('zh-CN', {
                  minimumFractionDigits: item.precision || 0,
                  maximumFractionDigits: item.precision || 0,
                })}
                <em>{item.suffix}</em>
              </strong>
              <p>{item.desc}</p>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card
            bordered={false}
            className="analytics-panel"
            title={<Space><FireOutlined />行为趋势</Space>}
            extra={<span className="analytics-refresh">更新 {formatRefreshTime(overview.generated_at)}</span>}
          >
            <Line
              height={300}
              data={trendData}
              xField="date"
              yField="value"
              seriesField="type"
              smooth
              point={{ size: 3 }}
              legend={{ position: 'top' }}
              color={['#1677ff', '#13c2c2', '#722ed1']}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card bordered={false} className="analytics-panel" title={<Space><FilterOutlined />事件分布</Space>}>
            <Pie
              height={300}
              data={eventDistribution}
              angleField="value"
              colorField="label"
              radius={0.82}
              innerRadius={0.58}
              legend={{ position: 'bottom' }}
              statistic={{ title: false, content: { content: `${eventDistribution.length}类` } }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="analytics-section">
        <Col xs={24} xl={8}>
          <Card bordered={false} className="analytics-panel" title="转化漏斗">
            <div className="analytics-funnel">
              {overview.funnel.map((item) => (
                <div className="analytics-funnel-item" key={item.step}>
                  <div>
                    <span>{item.step}</span>
                    <strong>{item.value.toLocaleString('zh-CN')}</strong>
                  </div>
                  <Progress percent={item.rate} strokeColor="#1677ff" trailColor="#edf2f7" />
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card bordered={false} className="analytics-panel" title="模块热度">
            <div className="analytics-heat-list">
              {overview.module_heat.map((item) => (
                <div className="analytics-heat-item" key={item.label}>
                  <div>
                    <Tag color={moduleColorMap[item.label] || 'default'}>{item.label}</Tag>
                    <strong>{item.value.toLocaleString('zh-CN')}</strong>
                  </div>
                  <span style={{ width: `${Math.max(8, (item.value / maxHeat) * 100)}%` }} />
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card bordered={false} className="analytics-panel" title="设备来源">
            <div className="analytics-device-list">
              {overview.device_distribution.map((item) => (
                <div className="analytics-device-item" key={item.label}>
                  <span>{item.label}</span>
                  <Progress percent={item.percent} strokeColor="#13c2c2" />
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      <Card bordered={false} className="analytics-panel analytics-section" title="事件明细">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={events}
          loading={loading}
          scroll={{ x: 980 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => `共 ${count} 条`,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
          locale={{ emptyText: '暂无埋点事件' }}
        />
      </Card>
    </div>
  );
};

export default AnalyticsDataPage;
